import React from "react";
import LegalPageTemplate from "./LegalPageTemplate";

const Complaints = () => {
  return (
    <LegalPageTemplate title="Complaints Policy">
      <div className="prose max-w-none space-y-8">
        {/* Introduction Section */}
        <section className="space-y-4">
          <h2 className="text-2xl font-bold text-primary mb-4">1. Introduction</h2>
          <p>
            Strawberry Loans ("Strawberry Loans, we or us") is committed to
            effective and efficient complaints management and to fair and
            transparent dealings in the financial marketplace.
          </p>
          <p>
            A complaint is an expression of dissatisfaction made to or about an
            organisation, related to its products, services, staff or the
            handling of a complaint, where a response or resolution is
            explicitly or implicitly expected or legally required.
          </p>
        </section>

        {/* Contact Information */}
        <section className="space-y-4">
          <h2 className="text-2xl font-bold text-primary mb-4">2. How to Contact Us</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-primary mb-3">General Complaints</h3>
              <div className="space-y-2">
                <p>
                  <span className="font-semibold">Email:</span>{" "}
                  <a
                    href="mailto:contact@strawberryloans.com.au"
                    className="text-primary hover:text-primary-700 transition-colors"
                  >
                    contact@strawberryloans.com.au
                  </a>
                </p>
                <p>
                  <span className="font-semibold">Phone:</span>{" "}
                  <a
                    href="tel:0438129991"
                    className="text-primary hover:text-primary-700 transition-colors"
                  >
                    0438 129 991
                  </a>
                </p>
                <p>
                  <span className="font-semibold">Post:</span> 184 Fisher Road
                  North, Cromer, NSW 2099
                </p>
              </div>
            </div>
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-primary mb-3">Hardship Complaints</h3>
              <div className="space-y-2">
                <p>
                  <span className="font-semibold">Email:</span>{" "}
                  <a
                    href="mailto:contact@strawberryloans.com.au"
                    className="text-primary hover:text-primary-700 transition-colors"
                  >
                    contact@strawberryloans.com.au
                  </a>
                </p>
                <p>
                  <span className="font-semibold">Phone:</span>{" "}
                  <a
                    href="tel:0438129991"
                    className="text-primary hover:text-primary-700 transition-colors"
                  >
                    0438 129 991
                  </a>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Complaints Process */}
        <section className="space-y-4">
          <h2 className="text-2xl font-bold text-primary mb-4">
            3. Our Complaints Management Process
          </h2>
          <div className="space-y-4">
            <p>
              We take your feedback seriously and will work proactively to
              investigate and resolve your complaint.
            </p>

            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-primary mb-3">Information Collection</h3>
              <p>We will collect the following information from you:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Your name</li>
                <li>Your contact details</li>
                <li>How you would prefer to be contacted</li>
                <li>A description of your complaint</li>
                <li>How you would like the complaint resolved</li>
              </ul>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-primary mb-3">Our Process</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  We will acknowledge your complaint within one (1) business day
                </li>
                <li>
                  We will provide you with the contact details of the person
                  responsible for dealing with your complaint
                </li>
                <li>
                  The responsible person will investigate and may require
                  further details from you
                </li>
                <li>
                  Upon completion of the investigation, you will receive an IDR
                  response containing:
                  <ul className="list-disc pl-6 mt-2">
                    <li>The final outcome of your complaint at IDR</li>
                    <li>
                      Your right to take the complaint to AFCA if you are not
                      satisfied
                    </li>
                  </ul>
                </li>
              </ul>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-primary mb-3">Response Requirements</h3>
              <p>
                Except where your complaint involves hardship, an IDR response
                is not required when a complaint is resolved by the end of the
                fifth (5th) business day of receipt, where we have:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Resolved the complaint to your satisfaction; or</li>
                <li>
                  Given you an explanation and/or apology when no further action
                  can be taken
                </li>
              </ul>
              <p>A written response will be provided if:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>You request a written response; or</li>
                <li>The complaint is about hardship</li>
              </ul>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-primary mb-3">Response Timeframes</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border p-2">Type of Complaint</th>
                      <th className="border p-2">Maximum Timeframe</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border p-2">
                        Complaints involving default notices
                      </td>
                      <td className="border p-2">
                        No later than twenty-one (21) calendar days after
                        receiving the complaint.
                      </td>
                    </tr>
                    <tr>
                      <td className="border p-2">
                        Hardship notices or enforcement proceedings
                      </td>
                      <td className="border p-2">
                        No later than twenty-one (21) calendar days after
                        receiving the complaint. Exceptions apply where we do
                        not have sufficient information to make a decision or if
                        an agreement is reached with you.
                      </td>
                    </tr>
                    <tr>
                      <td className="border p-2">All other complaints</td>
                      <td className="border p-2">
                        No later than thirty (30) calendar days after receiving
                        the complaint.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-sm italic">
                Exceptions apply where we do not have sufficient information to
                make a decision or if an agreement is reached with you.
              </p>
            </div>
          </div>
        </section>

        {/* External Dispute Resolution */}
        <section className="space-y-4">
          <h2 className="text-2xl font-bold text-primary mb-4">4. External Dispute Resolution</h2>
          <p>
            If your complaint is not resolved to your satisfaction through
            our IDR process, you have the right to refer your complaint to AFCA.
            AFCA is an independent and external dispute resolution scheme, of
            which Blue Dingo Finance is a member.
            <br />
            <br />
            <span className="font-semibold">
            You can lodge your complaint with AFCA by sending the relevant information and documents to:
            </span>
          </p>
          <div className="space-y-2">
            <p>
              <span className="font-semibold">Phone:</span>{" "}
              <a
                href="tel:1800931678"
                className="text-primary hover:text-primary-dark"
              >
                1800 931 678
              </a>
            </p>
            <p>
              <span className="font-semibold">Fax:</span> (03) 9613 6399
            </p>
            <p>
              <span className="font-semibold">Email:</span>{" "}
              <a
                href="mailto:info@afca.org.au"
                className="text-primary hover:text-primary-dark"
              >
                info@afca.org.au
              </a>
            </p>
            <p>
              <span className="font-semibold">Website:</span>{" "}
              <a
                href="https://www.afca.org.au"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:text-primary-dark"
              >
                www.afca.org.au
              </a>
            </p>
            <p>
              <span className="font-semibold">Post:</span> GPO Box 3, Melbourne
              VIC 3001
            </p>
          </div>
        </section>

        {/* Accessibility Services */}
        <section className="space-y-4">
          <h2 className="text-2xl font-bold text-primary mb-4">5. Accessibility Services</h2>
          <p>
            We take our commitment to provide accessible services to customers
            seriously.
          </p>
          <p>
            If you are deaf or have a hearing or speech impairment, you can
            contact us through the National Relay Service, a government
            initiative that offers an Australia-wide phone service for people
            who are deaf or have a hearing or speech impairment. It's available
            at no additional charge:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Talk to text users: 133 677 (then ask for 08 6800 0058)</li>
            <li>
              Speak to listen users: 1300 555 727 (then ask for 08 6800 0058)
            </li>
            <li>
              Internet relay users: Connect to NRS (then ask for 08 6800 0058)
            </li>
          </ul>
          <p>
            If you require this Policy in another language, please contact us
            using the details above.
          </p>
        </section>

        {/* Contact Information */}
        <section className="space-y-4">
          <h2 className="text-2xl font-bold text-primary mb-4">6. Contact Us</h2>
          <p>
            If you have any questions or would like further information about
            our complaints handling process, please contact us by:
          </p>
          <div className="space-y-2">
            <p>
              <span className="font-semibold">Email:</span>{" "}
              <a
                href="mailto:contact@strawberryloans.com.au"
                className="text-primary hover:text-primary-dark"
              >
                contact@strawberryloans.com.au
              </a>
            </p>
            <p>
              <span className="font-semibold">Phone:</span>{" "}
              <a
                href="tel:0438129991"
                className="text-primary hover:text-primary-dark"
              >
                0438 129 991
              </a>
            </p>
            <p>
              <span className="font-semibold">Post:</span> 184 Fisher Road
              North, Cromer, NSW 2099
            </p>
          </div>
        </section>
      </div>
    </LegalPageTemplate>
  );
};

export default Complaints;
